/**
 * App state: Zustand store with custom LocalStorage persistence so we can
 * round-trip Decimal/bigint/Date.
 *
 * Patch N: added addGoal/updateGoal/removeGoal CRUD actions for the goals
 * editor on the Dashboard.
 *
 * Patch O: system-wide undo. Every mutation action now snapshots the
 * persistable state BEFORE applying its change via withUndo() (see helper
 * below). Snapshots are kept in-memory only (undoStack on the store, NOT
 * persisted to localStorage) and capped at 50. Coalesced by action key
 * within 1 second windows so typing "1500" into a field is one undo step,
 * not four. The undo() action pops the top snapshot and restores it.
 *
 * What snapshots: every action that mutates persistable state (purchases,
 * goals, scenarios, ETF holdings/transactions, loan events, salary plan,
 * loop loan inputs, carry inputs, price model). What it does NOT: live
 * data fetches (background, not user actions), status/error state,
 * navigation (view, selectedScenarioId), UI prefs (paceMethod,
 * futurePlanWindowYears), or cloud sync hooks.
 *
 * Excluded from snapshots: liveSnapshot and lastSeedLoadedAt - rolling
 * those back on undo would briefly restore a 30-second-old BTC price or
 * trigger a re-seed. Both are transient/system state, not edits.
 *
 * Patch AA: View union updated to match the actual nav surface. Removed
 * 'future' (Future Plan was deleted in patch I but the union string had
 * lingered) and added 'relocate' (the new Relocate tab from patch W).
 * No persistence migration needed: `view` isn't part of the persisted
 * shape; it always initializes to 'dashboard' on a fresh load.
 *
 * Patch AH: added resetToDefaults() action. Wipes both in-memory state
 * AND the localStorage entry to engine defaults, without touching the
 * cloud row. AuthGate calls it before each cloud-load and after each
 * sign-out so the previous user's localStorage data can't leak into a
 * new session.
 *
 * Patch AI: emptied the ETF defaults. Pre-AI, defaultEtfHoldings and
 * defaultEtfTransactions hardcoded Paul's actual retirement-plan
 * balances. Paul-specific values now live in seed-loader.ts as
 * paulEtfHoldings / paulEtfTransactions and only flow into the store
 * via loadFromSeed (which is super-admin-gated by App.tsx post-AG).
 *
 * Patch AJ: two changes.
 *   1. Added a `welcomed` boolean to track whether the user has dismissed
 *      the first-sign-in welcome toast. Persisted (cloud-synced) so
 *      dismissing on one device doesn't re-show on another. resetToDefaults
 *      sets it back to false so a fresh account starts un-welcomed.
 *   2. Wired bainSalaryPlan() into loadFromSeed so "reset to seed"
 *      restores Paul's Bain consulting ladder, parallel to how AI added
 *      paulEtfHoldings / paulEtfTransactions. Pre-AJ the salary plan was
 *      the one slice loadFromSeed ignored, leaving it on whatever the
 *      user currently had. Now reset-to-seed is a real reset across
 *      every Paul-specific slice.
 *
 * Patch AL:
 *   1. Added `bullRallyInProjection: boolean` to LoopLoanInputs. Persisted
 *      so the user's choice to fold the bull-rally event into the chart
 *      survives reloads and syncs across devices.
 *   2. Changed yearlyRepaymentUsd default to 0. Pre-AL it was 12000, a
 *      vestigial workbook value that didn't actually affect the chart
 *      (projectLoopLoan never read the field). New users start at 0;
 *      existing users keep their persisted value until they clear it.
 *   3. Persisted-state init for loopLoanInputs now merges with
 *      defaultLoopLoanInputs() so older persisted shapes (lacking
 *      bullRallyInProjection) pick up the field with its default value
 *      on first AL load. Without this, ll.bullRallyInProjection would be
 *      undefined for pre-AL users, and TypeScript's strict-boolean check
 *      in the LoopLoan UI would render "false" but with type churn. The
 *      merge keeps the type clean and means we never need a separate
 *      migration step.
 *
 * Patch AM: View union gains 'admin' for the new Admin tab. Visibility
 * is gated in App.tsx and MobileNav.tsx by the role-derived flag from
 * useAuthStore; the union itself is unconditional so TypeScript can
 * verify the conditional render branches statically. View isn't
 * persisted (always initializes to 'dashboard'), so no migration.
 *
 * Patch AN: View union gains 'myPlan' for the new client-facing My Plan
 * tab. Unlike 'admin' this one's visible to everyone (empty state if
 * the user has no plans uploaded to them); no role gate. Same no-
 * persistence rationale applies.
 */

import { create } from 'zustand';
import { Decimal, ZERO } from '../engine/money.js';
import type { Goal, LiveBtcSnapshot, LiveAaveRateSnapshot, LiveLednRateSnapshot, LoanProvider, PurchaseTransaction, SalaryAssumption, EtfHolding, EtfTransaction, BtcPriceModel, LoanEvent, SalaryPlan, CustomBuy, AutoBuySchedule, SecurityAsset, SecurityTransaction, RealEstateProperty, CashEntry, DebtItem } from '../engine/types.js';

import { walkAutoBuySchedules, todayLocalNoonUtc } from '../engine/auto-buy.js';
import { WORKBOOK_PRICE_MODEL } from '../engine/price-model.js';
import type { PlannedEvent } from '../engine/future-plan.js';
import { expandSalary } from '../engine/future-plan.js';
import { defaultSalaryPlan, bainSalaryPlan } from '../engine/salary-plan.js';
import type { PaceMethod } from '../engine/purchases.js';
import { type DefiLlamaQuery, DEFAULT_DEFILLAMA_QUERY } from '../io/live/defillama.js';
import { encodeForStorage, decodeFromStorage } from '../schemas/persistence.js';
import { loadSeed, seedActualsToTransactions, seedGoalsToGoals, seedToScenarios, seedToLoopLoanInputs, seedToCarryInputs, paulEtfHoldings, paulEtfTransactions } from '../io/storage/seed-loader.js';
import { downloadExport, parseImport } from '../io/storage/export.js';
import { getCurrentSubdomain, isFIRETenant } from '../io/tenant.js';

const STORAGE_KEY = 'stackbtc:state:v1';
const UNDO_STACK_LIMIT = 50;
const COALESCE_WINDOW_MS = 1000;

type View = 'dashboard' | 'purchases' | 'security' | 'salaryPlan' | 'loopLoan' | 'carry' | 'assumptions' | 'powerlaw' | 'relocate' | 'news' | 'prism' | 'myPlan' | 'admin';

export interface ScenarioPlan {
  id: string;
  name: string;
  description: string;
  source: 'seed' | 'custom';
  events: PlannedEvent[];
  workbookFinalBtc?: Decimal;
}

export interface CarryInputs {
  /** Patch CH: which retirement-from-Bitcoin mechanic the page projects.
   *  'bbd' = buy/borrow/die. Borrow against the BTC for living expenses,
   *  never sell during life, heirs receive a stepped-up basis at death
   *  (death event itself out of scope of the in-life projection).
   *  No STRC, no income tax during life.
   *  'carry' = original STRC carry trade. Initial borrow buys STRC,
   *  dividends fund living expenses, basis depletion triggers cap-gains
   *  tax.
   *  Optional so older persisted state and seed-loader rows that
   *  predate Patch CH still type-check; the page falls back to 'bbd'
   *  when undefined. */
  strategy?: 'bbd' | 'carry';
  btcCollateral: Decimal;
  /** When 'career', btcCollateral is ignored and the page derives the
   *  effective value live from the career projection. Flips to 'manual'
   *  the moment the user edits the input. Optional so persisted state
   *  predating this field still type-checks; the page treats undefined
   *  as 'career' (auto-sync on for existing and new accounts). */
  btcCollateralMode?: 'career' | 'manual';
  initialLoanUsd: Decimal;
  lltv: Decimal;
  loanApr: Decimal;
  loanAprMode: 'live' | 'manual';
  strcDividendApr: Decimal;
  taxRate: Decimal;
  beginningMonthlyExpenses: Decimal;
  expenseGrowthApr: Decimal;
  inflationApr: Decimal;
  retirementStartMonth: number;
  loanAdditionStartMonth: number;
  loanAdditionStopMonth: number;
  firstLoanAddition: Decimal;
  loanAdditionGrowthApr: Decimal;
  loanRepaymentStartMonth: number;
  firstLoanRepayment: Decimal;
  loanRepaymentGrowthApr: Decimal;
  projectionMonths: number;
  startDate: Date;
  chartWindowYears: number;
  stressBtcPricePct: Decimal;
  stressDividendCutPct: Decimal;
  stressInterestShockPct: Decimal;
  stressExpenseShockPct: Decimal;
}

export interface LoopLoanInputs {
  btcCollateral: Decimal;
  loanUsd: Decimal;
  btcPriceUsd: Decimal;
  lltv: Decimal;
  yearlyRepaymentUsd: Decimal;
  athReferencePriceUsd: Decimal;
  availableCashUsd: Decimal;
  cashUsedForRepaymentPct: Decimal;
  safetyDrawdownThreshold: Decimal;
  bullBtcPriceUsd: Decimal;
  bullLoanIncreaseUsd: Decimal;
  /** Patch AL: when true, the long-term projection chart folds the
   *  bull-market rally event in. The rally fires at the first projected
   *  year where price >= bullBtcPriceUsd, adding bullLoanIncreaseUsd to
   *  the loan and converting it to BTC at that year's price. Persisted
   *  so the user's preference survives reloads and cross-device sync. */
  bullRallyInProjection: boolean;
  targetLtv: Decimal;
  stopLoopingBeforeYear: number;
  projectionYears: number;
  /** Patch BT: simulation start date. Pre-BT the engine was always given
   *  `new Date()` at the call site, making "today" the only possible
   *  start. Lifting it into the state lets the user simulate from a
   *  future or past anchor, matching the CarryRetirement startDate UX. */
  startDate: Date;
  loanApr: Decimal;
  loanAprMode: 'live' | 'manual';
  repaymentFormula?: 'fixed' | 'workbook';
}

interface AppState {
  // ----- data -----
  purchases: PurchaseTransaction[];
  goals: Goal[];
  liveSnapshot: LiveBtcSnapshot | null;
  lastSeedLoadedAt: Date | null;
  scenarios: ScenarioPlan[];
  selectedScenarioId: string | null;
  paceMethod: PaceMethod;
  futurePlanWindowYears: number;
  loopLoanInputs: LoopLoanInputs;
  aaveSnapshot: LiveAaveRateSnapshot | null;
  defillamaQuery: DefiLlamaQuery;
  carryInputs: CarryInputs;
  etfHoldings: EtfHolding[];
  etfTransactions: EtfTransaction[];
  priceModel: BtcPriceModel;
  loanEvents: LoanEvent[];
  salaryPlan: SalaryPlan;

  /** Patch BJ: non-Bitcoin tradable securities. Stocks, non-Bitcoin
   *  ETFs, mutual funds, bond/commodity ETFs, metal ETFs, and spot
   *  metals. Distinguished from etfHoldings (which is Bitcoin-ETF
   *  specific) because the new "Other assets" card in Purchases needs
   *  to cover a wider taxonomy. */
  securityAssets: SecurityAsset[];

  /** Patch BJ: buy/sell transaction log for securityAssets. Each row
   *  has direction='buy' or 'sell'; running balance per asset is
   *  sum(buys) - sum(sells). Same shape as EtfTransaction but with
   *  the explicit direction field. */
  securityTransactions: SecurityTransaction[];

  /** Patch BL: real-estate properties. Full market value lives in
   *  Assets on the Dashboard; mortgage balance (if any) lives in
   *  Liabilities. Standard balance-sheet view. */
  realEstateProperties: RealEstateProperty[];

  /** Patch BL: labeled cash holdings. List shape (vs single global
   *  number) so users can track multiple accounts. */
  cashEntries: CashEntry[];

  /** Patch BL: non-mortgage debt items. Mortgages live on
   *  realEstateProperties, intentionally; consumer-debt amortization
   *  is different enough that the separate model is cleaner. */
  debtItems: DebtItem[];

  /** Patch BB: per-asset auto-buy schedules. The walker
   *  (engine/auto-buy.ts) reads these and creates real PurchaseTransaction
   *  / EtfTransaction rows when scheduled dates pass. At most one
   *  schedule per (assetType, assetId) pair; upsertAutoBuy enforces. */
  autoBuySchedules: AutoBuySchedule[];

  /** Patch BE: which loan provider's rate the app treats as canonical
   *  for the borrow APR. The picker on Loop Loan / Carry / Salary /
   *  Purchases all mutate this single value, so a change on any page
   *  propagates everywhere. Persisted. */
  selectedLoanProvider: LoanProvider;

  /** Patch BE: live snapshot of Ledn's published rate. Hardcoded source
   *  (no API), fetched on app boot. Treated as "live" by the picker
   *  display for parity with aaveSnapshot. NOT persisted - rebuilt
   *  every session from the constants in io/live/ledn.ts. */
  lednSnapshot: LiveLednRateSnapshot | null;

  /** Patch BE: status of the most recent Ledn fetch. Mirrors the
   *  aaveStatus convention so the picker UI can show identical states
   *  regardless of which provider is selected. */
  lednStatus: 'idle' | 'loading' | 'ok' | 'error';
  lednError: string | null;

  /** Patch BE: a user-set borrow APR that overrides BOTH provider
   *  snapshots and the hardcoded 6% fallback. Set via the editable
   *  "Active APR" tile on the Purchases page. Cleared by the tile's
   *  reset affordance. Used as the fallbackApr to computeLoanState so
   *  the "Active APR" tile, new loan event defaults, and any other
   *  consumer of the canonical borrow rate respect the override.
   *  Persisted (it's a manual entry; survives reloads and devices). */
  manualBorrowAprOverride: Decimal | null;

  /** Patch AJ: has this user dismissed (or auto-faded past) the welcome
   *  toast? Persisted with the rest of state so dismissing on one device
   *  carries to the others. Defaults to false; set true by setWelcomed
   *  on either an explicit dismiss OR when the auto-fade timer fires. */
  welcomed: boolean;

  /** Patch BZ: per-item done flags on the Security page checklist. See
   *  toggleSecurityChecklistItem above. Empty object on a fresh account. */
  securityChecklist: Record<string, boolean>;

  // ----- ui -----
  view: View;
  liveStatus: 'idle' | 'loading' | 'ok' | 'stale' | 'error';
  liveError: string | null;
  aaveStatus: 'idle' | 'loading' | 'ok' | 'stale' | 'error';
  aaveError: string | null;
  etfPriceStatus: 'idle' | 'loading' | 'ok' | 'error';
  etfPriceError: string | null;

  // ----- undo (Patch O) -----
  /** Stack of serialized state snapshots, most recent at the end. Capped at
   *  UNDO_STACK_LIMIT. NOT persisted - undo history is intentionally a
   *  per-session concept; closing the tab discards it. */
  undoStack: string[];

  // ----- actions -----
  setView: (v: View) => void;

  addPurchase: (p: Omit<PurchaseTransaction, 'id'>) => void;
  updatePurchase: (id: string, patch: Partial<PurchaseTransaction>) => void;
  removePurchase: (id: string) => void;

  addGoal: (g: Omit<Goal, 'id'>) => void;
  updateGoal: (id: string, patch: Partial<Goal>) => void;
  removeGoal: (id: string) => void;

  setLiveSnapshot: (s: LiveBtcSnapshot | null) => void;
  setLiveStatus: (status: AppState['liveStatus'], error?: string | null) => void;

  selectScenario: (id: string) => void;
  setPaceMethod: (m: PaceMethod) => void;
  setFuturePlanWindowYears: (n: number) => void;
  createScenario: (name: string, sourceScenarioId?: string) => string;
  updateScenario: (id: string, patch: Partial<Pick<ScenarioPlan, 'name' | 'description'>>) => void;
  deleteScenario: (id: string) => void;
  upsertScenarioEvent: (scenarioId: string, index: number | null, event: PlannedEvent) => void;
  removeScenarioEvent: (scenarioId: string, index: number) => void;
  appendSalaryToScenario: (scenarioId: string, salary: SalaryAssumption, fromDate: Date, toDate: Date) => void;

  updateLoopLoanInputs: (patch: Partial<LoopLoanInputs>) => void;
  setAaveSnapshot: (s: LiveAaveRateSnapshot | null) => void;
  setAaveStatus: (status: AppState['aaveStatus'], error?: string | null) => void;
  // Patch BE: Ledn + provider selection + manual borrow APR override.
  // setLoanProvider drives which snapshot the picker treats as active;
  // setLednSnapshot / setLednStatus mirror the Aave shape so any polling
  // pattern that reads aaveSnapshot/aaveStatus has a parallel for Ledn.
  // setManualBorrowAprOverride accepts null to clear back to provider rate.
  setLoanProvider: (p: LoanProvider) => void;
  setLednSnapshot: (s: LiveLednRateSnapshot | null) => void;
  setLednStatus: (status: AppState['lednStatus'], error?: string | null) => void;
  setManualBorrowAprOverride: (apr: Decimal | null) => void;
  setDefillamaQuery: (q: DefiLlamaQuery) => void;
  updateCarryInputs: (patch: Partial<CarryInputs>) => void;

  loadFromSeed: () => Promise<void>;
  restoreScenarioFromSeed: (scenarioId: string) => Promise<void>;
  resetToSeed: () => Promise<void>;
  exportToJson: () => void;
  importFromJson: (text: string) => void;

  addEtfHolding: (holding: Omit<EtfHolding, 'id'>) => string;
  updateEtfHolding: (id: string, patch: Partial<EtfHolding>) => void;
  removeEtfHolding: (id: string) => void;
  addEtfTransaction: (tx: Omit<EtfTransaction, 'id'>) => void;
  updateEtfTransaction: (id: string, patch: Partial<EtfTransaction>) => void;
  removeEtfTransaction: (id: string) => void;

  // Patch DX: dedicated live-price setter for ETF holdings. Routes
  // around saveToStorage and the undo system because the snapshot is
  // background-fetched transient data, not a user edit. Pre-DX the
  // ETF refresh path called updateEtfHolding which fired cloud saves
  // on every 2-minute interval tick + every tab-visibility regain;
  // see same fix in DU for setLiveSnapshot.
  setEtfLivePrice: (id: string, snap: { priceUsd: Decimal; fetchedAt: Date } | null) => void;

  // Patch BJ: security CRUD. Mirrors the ETF actions almost 1:1 (same
  // shape, same return semantics) so callers can swap between EtfHolding
  // and SecurityAsset without ceremony. Differences: SecurityTransaction
  // has a direction field, addEtfHolding kicks off a background price
  // fetch via the lazy import pattern (Patch BB), addSecurityAsset
  // does the same.
  addSecurityAsset: (asset: Omit<SecurityAsset, 'id'>) => string;
  updateSecurityAsset: (id: string, patch: Partial<SecurityAsset>) => void;
  removeSecurityAsset: (id: string) => void;
  addSecurityTransaction: (tx: Omit<SecurityTransaction, 'id'>) => void;
  updateSecurityTransaction: (id: string, patch: Partial<SecurityTransaction>) => void;
  removeSecurityTransaction: (id: string) => void;

  // Patch DX: dedicated live-price setter for security assets, paired
  // with setEtfLivePrice. Same reasoning - bypass save and undo for
  // transient background-fetched data.
  setSecurityLivePrice: (id: string, snap: { priceUsd: Decimal; fetchedAt: Date } | null) => void;

  // Patch BL: real estate / cash / debt CRUD. All three are simple
  // list-of-items shapes, no per-item background workers.
  addRealEstateProperty: (p: Omit<RealEstateProperty, 'id'>) => string;
  updateRealEstateProperty: (id: string, patch: Partial<RealEstateProperty>) => void;
  removeRealEstateProperty: (id: string) => void;
  addCashEntry: (c: Omit<CashEntry, 'id'>) => string;
  updateCashEntry: (id: string, patch: Partial<CashEntry>) => void;
  removeCashEntry: (id: string) => void;
  addDebtItem: (d: Omit<DebtItem, 'id'>) => string;
  updateDebtItem: (id: string, patch: Partial<DebtItem>) => void;
  removeDebtItem: (id: string) => void;
  setEtfPriceStatus: (status: AppState['etfPriceStatus'], error?: string | null) => void;
  updatePriceModel: (patch: Partial<BtcPriceModel>) => void;
  resetPriceModel: () => void;

  addLoanEvent: (e: Omit<LoanEvent, 'id'>) => void;
  updateLoanEvent: (id: string, patch: Partial<LoanEvent>) => void;
  removeLoanEvent: (id: string) => void;

  updateSalaryPlan: (patch: Partial<SalaryPlan>) => void;
  updateSalaryLevel: (id: string, patch: Partial<import('../engine/types.js').SalaryLevel>) => void;
  reorderSalaryLevels: (idsInOrder: string[]) => void;
  resetSalaryPlan: () => void;
  setSalaryBreak: (afterLevelIndex: number, breakStart: Date, breakEnd: Date) => void;
  clearSalaryBreak: () => void;

  /** Patch AW: custom-buy actions. Add returns the new id so the UI can
   *  focus the new row's first input. Update / remove accept the id and
   *  apply to whatever's currently in salaryPlan.customBuys. */
  addCustomBuy: (partial?: Partial<Omit<CustomBuy, 'id'>>) => string;
  updateCustomBuy: (id: string, patch: Partial<Omit<CustomBuy, 'id'>>) => void;
  removeCustomBuy: (id: string) => void;

  /** Patch BB: per-asset auto-buy schedule actions.
   *
   *  upsertAutoBuy: if a schedule exists for (assetType, assetId), patch
   *  it; otherwise create a new one merging the patch onto sensible
   *  defaults. Toggling enabled from false to true on an EXISTING
   *  schedule also resets lastPopulatedDate to "now" so the user doesn't
   *  get a surprise backfill of all the dates that passed while disabled.
   *  Initial creation with enabled=true does NOT reset lastPopulatedDate
   *  so users can deliberately backfill from a past startDate.
   *
   *  removeAutoBuy: drop the schedule entirely. Already-created rows
   *  stay in the user's history (per spec).
   *
   *  runAutoBuyWalker: invoke the engine walker, apply its output to
   *  state in a single withUndo step. Idempotent: re-running with no
   *  new scheduled dates is a no-op. Caller is App.tsx (fires on
   *  liveSnapshot ready).
   */
  upsertAutoBuy: (assetType: 'btc' | 'etf' | 'security', assetId: string, patch: Partial<Omit<AutoBuySchedule, 'id' | 'assetType' | 'assetId'>>) => void;
  removeAutoBuy: (assetType: 'btc' | 'etf' | 'security', assetId: string) => void;
  runAutoBuyWalker: () => void;

  /** Patch AJ: persist the welcome-toast dismissal flag. Both the
   *  explicit X-button dismiss and the auto-fade timer call this with
   *  true. Could in principle be reset to false to re-show the toast,
   *  but there's no UI affordance for that today. */
  setWelcomed: (v: boolean) => void;

  /** Patch BZ: per-item done flags for the practical checklist on the
   *  Security page. Keyed by stable string id (one per checklist item).
   *  Persisted + cloud-synced like everything else in this store, so
   *  ticking an item on desktop shows up checked on mobile. Plain
   *  Record<string, boolean> so it survives JSON round-trips with no
   *  Decimal/Date tagging needed. */
  toggleSecurityChecklistItem: (id: string) => void;

  /** Patch O: pop the top snapshot off the undo stack and restore it.
   *  No-op when the stack is empty. */
  undo: () => void;

  /** Patch AH: full reset of in-memory state and localStorage to engine
   *  defaults. Does NOT touch the cloud row. AuthGate calls this around
   *  sign-in/sign-out to prevent localStorage-level cross-user contamination. */
  resetToDefaults: () => void;

  applyPersistedState: (encoded: unknown) => void;
  snapshotForCloud: () => unknown;

  // ---- Patch AS: admin client-view ----
  /** Non-null while the admin is browsing a cohort member's data in a
   *  read-only view-as session. UI uses this to render the banner, swap
   *  the Admin nav button to "Exit view", and gate edit affordances.
   *  Session-only: not persisted to localStorage or cloud (decode/encode
   *  pipelines ignore it). */
  viewingAs: { userId: string; email: string } | null;

  /** Enter view-as mode. Snapshots the admin's current state to a sidecar
   *  localStorage key, applies the client's state to the main store, sets
   *  viewingAs. The sync.ts viewingAs flag is also flipped so no debounced
   *  save can fire mid-session and clobber the admin's cloud row. */
  enterViewAs: (args: { userId: string; email: string; clientState: unknown }) => void;

  /** Exit view-as mode. Reads the admin snapshot back from the sidecar
   *  localStorage key, applies it, clears viewingAs and the sync gate.
   *  If the snapshot is missing (e.g. cross-tab edge case), falls back
   *  to resetToDefaults and lets the caller force a cloud reload. */
  exitViewAs: () => void;
}

// ---- LocalStorage helpers ----

function defaultLoopLoanInputs(): LoopLoanInputs {
  return {
    btcCollateral: new Decimal('0.5'),
    loanUsd: new Decimal(20000),
    btcPriceUsd: new Decimal(76463),
    lltv: new Decimal('0.78'),
    // Patch AL: yearly repayment defaults to 0. The long-term projection
    // chart doesn't apply repayments anyway (projectLoopLoan never reads
    // this field), so non-zero values were misleading UI clutter.
    yearlyRepaymentUsd: new Decimal(0),
    athReferencePriceUsd: new Decimal(126272),
    availableCashUsd: new Decimal(50000),
    cashUsedForRepaymentPct: new Decimal('1.0'),
    safetyDrawdownThreshold: new Decimal('-0.6'),
    bullBtcPriceUsd: new Decimal(150000),
    bullLoanIncreaseUsd: new Decimal(20000),
    // Patch AL: bull-rally toggle off by default. User opts in via the
    // checkbox in the bull-market section of the LoopLoan page.
    bullRallyInProjection: false,
    targetLtv: new Decimal('0.50'),
    stopLoopingBeforeYear: 3,
    projectionYears: 8,
    // Patch BT: default startDate is today. The defaults-merge in
    // persistence load fills this for pre-BT users (whose persisted
    // shape lacks the field) without resetting their other choices.
    startDate: new Date(),
    loanApr: new Decimal('0.085'),
    loanAprMode: 'live',
    repaymentFormula: 'fixed',
  };
}

function defaultCarryInputs(): CarryInputs {
  return {
    strategy: 'bbd',
    btcCollateral: new Decimal('2.1'),
    btcCollateralMode: 'career',
    initialLoanUsd: new Decimal(900000),
    lltv: new Decimal('0.78'),
    loanApr: new Decimal('0.05'),
    // Patch CD: default to live mode so the field auto-syncs to the
    // selected provider's borrow rate on first render. The 'manual'
    // sentinel kicks in the moment the user types in the Loan APR
    // input. Previously defaulted to 'manual' but the field was never
    // wired to the live snapshot so it stayed at this seed value forever.
    loanAprMode: 'live',
    strcDividendApr: new Decimal('0.1075'),
    taxRate: new Decimal('0.19'),
    beginningMonthlyExpenses: new Decimal(3000),
    expenseGrowthApr: new Decimal('0.07'),
    inflationApr: new Decimal('0.03'),
    retirementStartMonth: 1,
    loanAdditionStartMonth: 1,
    loanAdditionStopMonth: 10000,
    firstLoanAddition: new Decimal(0),
    loanAdditionGrowthApr: new Decimal('0.07'),
    loanRepaymentStartMonth: 170,
    firstLoanRepayment: new Decimal(0),
    loanRepaymentGrowthApr: new Decimal(0),
    projectionMonths: 840,
    // Patch AV: bumped default retirement start from 2033 to 2036. Reflects
    // the more realistic 10-year accumulation horizon the BRA cohort target
    // (45-55yo) tends to plan against. The Carry Retirement page exposes
    // the date input under "Retirement start date" so any user can override
    // this for their own timeline; this is just the seed value for new
    // accounts and reset-to-defaults.
    startDate: new Date('2036-09-21T04:48:00Z'),
    chartWindowYears: 10,
    stressBtcPricePct: new Decimal(0),
    stressDividendCutPct: new Decimal(0),
    stressInterestShockPct: new Decimal(0),
    stressExpenseShockPct: new Decimal(0),
  };
}

/** Patch AI: empty by default. Paul-specific values live in seed-loader.ts
 *  as paulEtfHoldings(), only applied via loadFromSeed (super-admin-gated). */
function defaultEtfHoldings(): EtfHolding[] {
  return [];
}

/** Patch AI: empty by default. paulEtfTransactions() in seed-loader carries
 *  the 296 / 37 share starting balances that used to live here. */
function defaultEtfTransactions(): EtfTransaction[] {
  return [];
}

function encodeStateForPersist(state: Pick<AppState,
  'purchases' | 'goals' | 'liveSnapshot' | 'lastSeedLoadedAt' |
  'scenarios' | 'selectedScenarioId' | 'paceMethod' | 'futurePlanWindowYears' | 'loopLoanInputs' | 'defillamaQuery' | 'carryInputs' |
  'etfHoldings' | 'etfTransactions' | 'priceModel' | 'loanEvents' | 'salaryPlan' | 'autoBuySchedules' |
  'selectedLoanProvider' | 'manualBorrowAprOverride' | 'welcomed' |
  'securityAssets' | 'securityTransactions' |
  'realEstateProperties' | 'cashEntries' | 'debtItems' |
  'securityChecklist'
>): unknown {
  return encodeForStorage({
    purchases: state.purchases,
    goals: state.goals,
    liveSnapshot: state.liveSnapshot,
    lastSeedLoadedAt: state.lastSeedLoadedAt,
    scenarios: state.scenarios,
    selectedScenarioId: state.selectedScenarioId,
    paceMethod: state.paceMethod,
    futurePlanWindowYears: state.futurePlanWindowYears,
    loopLoanInputs: state.loopLoanInputs,
    defillamaQuery: state.defillamaQuery,
    carryInputs: state.carryInputs,
    etfHoldings: state.etfHoldings,
    etfTransactions: state.etfTransactions,
    priceModel: state.priceModel,
    loanEvents: state.loanEvents,
    salaryPlan: state.salaryPlan,
    autoBuySchedules: state.autoBuySchedules,
    // Patch BE: persist provider selection + manual APR override. The
    // Ledn snapshot is NOT persisted (it's rebuilt on boot from
    // io/live/ledn.ts since the rates are hardcoded in code, not
    // fetched from a live API).
    selectedLoanProvider: state.selectedLoanProvider,
    manualBorrowAprOverride: state.manualBorrowAprOverride,
    // Patch BJ: persist the new security slices. Both round-trip through
    // encodeForStorage's recursive tagger for Decimal + Date fields.
    securityAssets: state.securityAssets,
    securityTransactions: state.securityTransactions,
    // Patch BL: persist real estate / cash / debt. Mortgage shape inside
    // RealEstateProperty has nested Decimal + Date fields; the recursive
    // encoder handles them.
    realEstateProperties: state.realEstateProperties,
    cashEntries: state.cashEntries,
    debtItems: state.debtItems,
    welcomed: state.welcomed,
    // Patch BZ: per-item done flags for the Security-page checklist.
    // Plain object of booleans; encodeForStorage passes through untouched.
    securityChecklist: state.securityChecklist,
  });
}

/** Patch O: undo-snapshot encoder. Same as encodeStateForPersist but EXCLUDES
 *  liveSnapshot and lastSeedLoadedAt. Rationale: liveSnapshot is the BTC
 *  price (background-fetched, would be 30s+ stale on undo restore);
 *  lastSeedLoadedAt is a system flag that decides whether to auto-seed on
 *  first load. Neither should roll back when the user undoes an edit.
 *  Patch AJ: also excludes welcomed; the welcome toast is a one-shot UI
 *  state, not an edit, and undoing into "un-welcomed" would re-show it
 *  which would be jarring. */
function encodeStateForUndo(state: AppState): unknown {
  return encodeForStorage({
    purchases: state.purchases,
    goals: state.goals,
    scenarios: state.scenarios,
    selectedScenarioId: state.selectedScenarioId,
    paceMethod: state.paceMethod,
    futurePlanWindowYears: state.futurePlanWindowYears,
    loopLoanInputs: state.loopLoanInputs,
    defillamaQuery: state.defillamaQuery,
    carryInputs: state.carryInputs,
    etfHoldings: state.etfHoldings,
    etfTransactions: state.etfTransactions,
    priceModel: state.priceModel,
    loanEvents: state.loanEvents,
    salaryPlan: state.salaryPlan,
    autoBuySchedules: state.autoBuySchedules,
    // Patch BE: included in undo snapshots so reverting a provider
    // switch (or a manual APR override clear) rolls back correctly.
    selectedLoanProvider: state.selectedLoanProvider,
    manualBorrowAprOverride: state.manualBorrowAprOverride,
    // Patch BJ: security slices included in undo so add/remove/edit on
    // securities and their transactions is reversible.
    securityAssets: state.securityAssets,
    securityTransactions: state.securityTransactions,
    // Patch BL: real estate / cash / debt included in undo for the same
    // reason - adds/removes are reversible from the header Undo.
    realEstateProperties: state.realEstateProperties,
    cashEntries: state.cashEntries,
    debtItems: state.debtItems,
  });
}

// Patch CF: input-lag fix. Pre-CF, every store mutation ran
// encodeStateForPersist + JSON.stringify + localStorage.setItem
// synchronously on the same task as the React state set. For a state
// of moderate size (purchases + salary + scenarios + ETF + securities +
// real estate + cash + debt) the encode walks dozens of Decimal/Date
// instances and the stringify chews through hundreds of KB, blocking
// the main thread for tens to hundreds of ms per keystroke. Combined
// with the expensive useMemo recomputes on CarryRetirement /
// SalaryPlan / LoopLoan, typing felt like it lagged by seconds.
//
// Now: hold the latest state in pendingLocalState, schedule a single
// 250 ms flush, and coalesce all writes that arrive inside the window
// into one. The cloud save is scheduled from the flush handler so it
// also rides the debounce. beforeunload flushes synchronously so a
// quick edit-then-close doesn't lose data.
let pendingLocalState: PartialAppState | null = null;
let pendingLocalSaveTimer: ReturnType<typeof setTimeout> | null = null;
const LOCAL_SAVE_DEBOUNCE_MS = 250;

type PartialAppState = Parameters<typeof encodeStateForPersist>[0];

function flushPendingLocalSave(): void {
  if (pendingLocalSaveTimer !== null) {
    clearTimeout(pendingLocalSaveTimer);
    pendingLocalSaveTimer = null;
  }
  if (pendingLocalState === null) return;
  const state = pendingLocalState;
  pendingLocalState = null;
  try {
    const payload = encodeStateForPersist(state);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    void scheduleCloudSaveIfSignedIn(payload);
  } catch (e) {
    console.warn('Failed to persist state', e);
  }
}

if (typeof window !== 'undefined') {
  // Patch CF: flush any pending debounced local save before the page
  // unloads so a fast edit-then-close doesn't lose the keystroke. Cloud
  // save already has its own beforeunload flush installed by AuthGate
  // (installBeforeUnloadFlush from sync.ts, Patch BQ); this is the
  // local-storage counterpart.
  window.addEventListener('beforeunload', flushPendingLocalSave);
}

function saveToStorage(state: PartialAppState): void {
  pendingLocalState = state;
  if (pendingLocalSaveTimer === null) {
    pendingLocalSaveTimer = setTimeout(flushPendingLocalSave, LOCAL_SAVE_DEBOUNCE_MS);
  }
}

async function scheduleCloudSaveIfSignedIn(payload: unknown): Promise<void> {
  try {
    const [{ useAuthStore }, { scheduleCloudSave }, { getCurrentSubdomain }] = await Promise.all([
      import('./auth.js'),
      import('../io/supabase/sync.js'),
      import('../io/tenant.js'),
    ]);
    const userId = useAuthStore.getState().user?.id ?? null;
    const tenant = getCurrentSubdomain() ?? 'rajat';
    scheduleCloudSave(userId, tenant, payload);
  } catch {
    // Cloud is optional; failures here are non-fatal.
  }
}

// Patch AP: immediate cloud save for recovery paths. Used by resetToSeed
// and importFromJson, which need their writes to commit regardless of
// the 1500ms debounce. Pre-AP, both could lose their write if a
// setLiveSnapshot fired within the debounce window and the resulting
// scheduleCloudSave call cancelled the recovery-write's timer; the
// captured-state-by-value protected the content, but the cancellation
// still meant the recovery never reached cloud. saveStateToCloudImmediate
// in sync.ts now bypasses both the debounce and the cloudLoaded gate.
async function saveImmediatelyIfSignedIn(payload: unknown): Promise<void> {
  try {
    const [{ useAuthStore }, { saveStateToCloudImmediate }, { getCurrentSubdomain }] = await Promise.all([
      import('./auth.js'),
      import('../io/supabase/sync.js'),
      import('../io/tenant.js'),
    ]);
    const userId = useAuthStore.getState().user?.id ?? null;
    if (!userId) return;
    const tenant = getCurrentSubdomain() ?? 'rajat';
    await saveStateToCloudImmediate(userId, tenant, payload);
  } catch (e) {
    // Non-fatal: the user still has the change in local store. They can
    // re-export to a file if cloud propagation is genuinely broken.
    console.warn('Immediate cloud save failed', e);
  }
}

interface PersistedShape {
  purchases?: unknown[];
  goals?: unknown[];
  liveSnapshot?: unknown;
  lastSeedLoadedAt?: unknown;
  scenarios?: unknown[];
  selectedScenarioId?: unknown;
  paceMethod?: unknown;
  futurePlanWindowYears?: unknown;
  loopLoanInputs?: unknown;
  defillamaQuery?: unknown;
  carryInputs?: unknown;
  etfHoldings?: unknown[];
  etfTransactions?: unknown[];
  priceModel?: unknown;
  loanEvents?: unknown[];
  salaryPlan?: unknown;
  autoBuySchedules?: unknown[];
  // Patch BE
  selectedLoanProvider?: unknown;
  manualBorrowAprOverride?: unknown;
  // Patch BJ
  securityAssets?: unknown[];
  securityTransactions?: unknown[];
  // Patch BL
  realEstateProperties?: unknown[];
  cashEntries?: unknown[];
  debtItems?: unknown[];
  welcomed?: unknown;
}

function decodePersistedState(parsed: unknown): Partial<AppState> {
  const decoded = decodeFromStorage(parsed) as PersistedShape;
  const out: Partial<AppState> = {};
  if (Array.isArray(decoded.purchases)) {
    out.purchases = decoded.purchases as PurchaseTransaction[];
  }
  if (Array.isArray(decoded.goals)) {
    out.goals = decoded.goals as Goal[];
  }
  if (decoded.liveSnapshot && typeof decoded.liveSnapshot === 'object') {
    out.liveSnapshot = decoded.liveSnapshot as LiveBtcSnapshot;
  }
  if (decoded.lastSeedLoadedAt instanceof Date) {
    out.lastSeedLoadedAt = decoded.lastSeedLoadedAt;
  }
  if (Array.isArray(decoded.scenarios)) {
    out.scenarios = decoded.scenarios as ScenarioPlan[];
  }
  if (typeof decoded.selectedScenarioId === 'string') {
    out.selectedScenarioId = decoded.selectedScenarioId;
  }
  if (decoded.paceMethod === 'allTimePace' || decoded.paceMethod === 'recentPace') {
    out.paceMethod = decoded.paceMethod;
  }
  if (typeof decoded.futurePlanWindowYears === 'number' && decoded.futurePlanWindowYears > 0) {
    out.futurePlanWindowYears = decoded.futurePlanWindowYears;
  }
  if (decoded.loopLoanInputs && typeof decoded.loopLoanInputs === 'object') {
    // Patch AL: merge persisted state with current defaults so old shapes
    // (lacking bullRallyInProjection or other AL-added fields) get the
    // missing defaults filled in. Persisted fields win where they exist;
    // defaults fill the gaps. Keeps the type clean (LoopLoanInputs is
    // required-only, no optional fields besides repaymentFormula).
    out.loopLoanInputs = {
      ...defaultLoopLoanInputs(),
      ...(decoded.loopLoanInputs as Partial<LoopLoanInputs>),
    };
  }
  if (decoded.defillamaQuery && typeof decoded.defillamaQuery === 'object') {
    out.defillamaQuery = decoded.defillamaQuery as DefiLlamaQuery;
  }
  if (decoded.carryInputs && typeof decoded.carryInputs === 'object') {
    out.carryInputs = decoded.carryInputs as CarryInputs;
  }
  if (Array.isArray(decoded.etfHoldings)) {
    out.etfHoldings = decoded.etfHoldings as EtfHolding[];
  }
  if (Array.isArray(decoded.etfTransactions)) {
    out.etfTransactions = decoded.etfTransactions as EtfTransaction[];
  }
  if (decoded.priceModel && typeof decoded.priceModel === 'object') {
    // Patch BS migration: pre-BS priceModel had fields startDate /
    // startPriceUsd / initialDoublingPeriodDays / increaseInTimeToDouble
    // (Santostasi stretching-doublings). Post-BS uses anchorDate /
    // exponent / logIntercept (pure power law). If we see the old shape,
    // skip the apply so the store falls back to WORKBOOK_PRICE_MODEL.
    // Users lose their customizations (few had any), but the alternative
    // is leaking undefined fields into the new code path and crashing.
    const pm = decoded.priceModel as Record<string, unknown>;
    const isOldShape = 'initialDoublingPeriodDays' in pm || 'increaseInTimeToDouble' in pm;
    if (!isOldShape) {
      out.priceModel = decoded.priceModel as BtcPriceModel;
    }
  }
  if (Array.isArray(decoded.loanEvents)) {
    out.loanEvents = decoded.loanEvents as LoanEvent[];
  }
  if (decoded.salaryPlan && typeof decoded.salaryPlan === 'object') {
    out.salaryPlan = decoded.salaryPlan as SalaryPlan;
  }
  // Patch BB: AutoBuySchedule[] round-trips through encodeForStorage with
  // no custom logic (its Decimal + Date fields are handled by the
  // recursive encoder). Just guard the type cast on shape presence.
  if (Array.isArray(decoded.autoBuySchedules)) {
    out.autoBuySchedules = decoded.autoBuySchedules as AutoBuySchedule[];
  }
  // Patch BJ: security slices. Same recursive-encoder coverage handles
  // their Decimal + Date fields; just type-cast on shape presence.
  if (Array.isArray(decoded.securityAssets)) {
    out.securityAssets = decoded.securityAssets as SecurityAsset[];
  }
  if (Array.isArray(decoded.securityTransactions)) {
    out.securityTransactions = decoded.securityTransactions as SecurityTransaction[];
  }
  // Patch BL: real estate, cash, debt. Same recursive-encoder coverage.
  if (Array.isArray(decoded.realEstateProperties)) {
    out.realEstateProperties = decoded.realEstateProperties as RealEstateProperty[];
  }
  if (Array.isArray(decoded.cashEntries)) {
    out.cashEntries = decoded.cashEntries as CashEntry[];
  }
  if (Array.isArray(decoded.debtItems)) {
    out.debtItems = decoded.debtItems as DebtItem[];
  }
  // Patch BE: provider selection + manual APR override decode.
  // selectedLoanProvider is a string union; tolerate other strings by
  // defaulting to 'aave' rather than crashing. manualBorrowAprOverride
  // round-trips through decodeFromStorage already (Decimal-tagged) so
  // we just check the resulting type.
  if (decoded.selectedLoanProvider === 'aave' || decoded.selectedLoanProvider === 'ledn') {
    out.selectedLoanProvider = decoded.selectedLoanProvider;
  }
  if (decoded.manualBorrowAprOverride instanceof Decimal) {
    out.manualBorrowAprOverride = decoded.manualBorrowAprOverride;
  } else if (decoded.manualBorrowAprOverride === null) {
    out.manualBorrowAprOverride = null;
  }
  if (typeof decoded.welcomed === 'boolean') {
    out.welcomed = decoded.welcomed;
  }
  return out;
}

function loadFromStorage(): Partial<AppState> | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    return decodePersistedState(parsed);
  } catch (e) {
    console.warn('Failed to load persisted state', e);
    return null;
  }
}

// ---- Patch O: undo coalesce tracking ----
//
// These live as closure variables (NOT on AppState) because they're internal
// bookkeeping with no observable purpose for consumers. Keeping them off
// the state object also avoids triggering re-renders when only the
// timestamp changes. They're per-page-load (no persistence) which matches
// the per-session semantics of undoStack itself.
let lastSnapshotKey = '';
let lastSnapshotTime = 0;

/**
 * Build the next-state shape with an undo snapshot of the prior state pushed
 * onto undoStack, coalescing same-key snapshots within COALESCE_WINDOW_MS.
 *
 * Usage in a mutation action:
 *   set(s => {
 *     const next = withUndo(s, 'purchase:add', { purchases: [...] });
 *     saveToStorage({ ...s, ...next });
 *     return next;
 *   });
 *
 * Coalescing means typing "1500" into one field (4 onChange events) creates
 * one undo step, not four; the key includes the entity ID so different rows
 * stay independent. Structural changes (add/remove rows) use static keys
 * like 'purchase:add' that won't coalesce with edits, so adding 3 rows in
 * 5 seconds = 3 undo steps.
 */
function withUndo<T extends object>(s: AppState, key: string, next: T): T & { undoStack: string[] } {
  const now = Date.now();
  let undoStack: string[];
  if (lastSnapshotKey === key && now - lastSnapshotTime < COALESCE_WINDOW_MS) {
    // Same edit session, same field/entity. Don't push a new snapshot;
    // the existing top-of-stack already represents the state before this
    // edit session began. Just bump the coalesce timestamp.
    undoStack = s.undoStack;
  } else {
    // Different action or beyond the coalesce window. Capture s as it is
    // RIGHT NOW (before the mutation we're about to apply via `next`).
    let snapshotJson: string;
    try {
      snapshotJson = JSON.stringify(encodeStateForUndo(s));
    } catch (e) {
      console.warn('Undo snapshot failed:', e);
      // If serialization fails, fall through with the existing stack
      // unchanged. Better than crashing the mutation.
      lastSnapshotKey = key;
      lastSnapshotTime = now;
      return { ...next, undoStack: s.undoStack };
    }
    // Cap the stack: keep the newest UNDO_STACK_LIMIT entries, drop the
    // oldest if we're at capacity.
    undoStack = [...s.undoStack.slice(-(UNDO_STACK_LIMIT - 1)), snapshotJson];
  }
  lastSnapshotKey = key;
  lastSnapshotTime = now;
  return { ...next, undoStack };
}

// ---- store ----

const persisted = typeof window !== 'undefined' ? loadFromStorage() : null;

export const useAppStore = create<AppState>((set, get) => ({
  purchases: persisted?.purchases ?? [],
  goals: persisted?.goals ?? (isFIRETenant() ? [
    { id: 'default-fire-1', label: 'Whole coiner', targetBtc: new Decimal('1'), enabled: true },
    { id: 'default-fire-2', label: '2.1 BTC', targetBtc: new Decimal('2.1'), enabled: true },
    { id: 'default-fire-3', label: '10 BTC', targetBtc: new Decimal('10'), enabled: true },
  ] : []),
  liveSnapshot: persisted?.liveSnapshot ?? null,
  lastSeedLoadedAt: persisted?.lastSeedLoadedAt ?? null,
  scenarios: persisted?.scenarios ?? [],
  selectedScenarioId: persisted?.selectedScenarioId ?? null,
  paceMethod: persisted?.paceMethod ?? 'allTimePace',
  futurePlanWindowYears: persisted?.futurePlanWindowYears ?? 5,
  loopLoanInputs: persisted?.loopLoanInputs ?? defaultLoopLoanInputs(),
  aaveSnapshot: null,
  defillamaQuery: persisted?.defillamaQuery ?? DEFAULT_DEFILLAMA_QUERY,
  carryInputs: persisted?.carryInputs ?? (isFIRETenant()
    ? { ...defaultCarryInputs(), strategy: 'carry' as const }
    : defaultCarryInputs()),
  etfHoldings: persisted?.etfHoldings ?? defaultEtfHoldings(),
  etfTransactions: persisted?.etfTransactions ?? defaultEtfTransactions(),
  // Patch BJ: empty by default. Users add securities through the new
  // "Other assets" card sub-cards. No paul-specific seed for now;
  // can add one later if super-admin seed coverage is wanted.
  securityAssets: persisted?.securityAssets ?? [],
  securityTransactions: persisted?.securityTransactions ?? [],
  // Patch BL: real estate / cash / debt empty by default. Same rationale
  // as BJ - super-admin can seed if/when wanted, but users self-populate.
  realEstateProperties: persisted?.realEstateProperties ?? [],
  cashEntries: persisted?.cashEntries ?? [],
  debtItems: persisted?.debtItems ?? [],
  priceModel: persisted?.priceModel ?? WORKBOOK_PRICE_MODEL,
  loanEvents: persisted?.loanEvents ?? [],
  // One-time migration: bradlong accounts seeded with the old defaultIncomeStagePlan()
  // (first level name "Current income") get silently upgraded to the VP/SVP/EVP ladder
  // so they match rajat. Safe: "Current income" is not a plausible user-entered role name.
  salaryPlan: (() => {
    const p = persisted?.salaryPlan ?? defaultSalaryPlan();
    if (getCurrentSubdomain() === 'bradlong' && p.levels[0]?.name === 'Current income') {
      return defaultSalaryPlan();
    }
    return p;
  })(),
  // Patch BB: hydrate from persisted state if present, else empty. The
  // walker no-ops on an empty array, so first-time users get a benign
  // default until they enable a schedule.
  autoBuySchedules: persisted?.autoBuySchedules ?? [],
  // Patch BE: provider selection + Ledn snapshot + manual override.
  // selectedLoanProvider is persisted (user preference). Default 'aave'
  // matches the pre-BE behavior where Aave was the only provider.
  // lednSnapshot is NOT persisted - it's a "live" snapshot rebuilt on
  // boot from io/live/ledn.ts (which is itself hardcoded; the App-level
  // useEffect that calls fetchLednBorrow populates it). manualBorrow
  // AprOverride IS persisted - it's a user-entered manual value.
  selectedLoanProvider: persisted?.selectedLoanProvider ?? 'aave',
  lednSnapshot: null,
  lednStatus: 'idle',
  lednError: null,
  manualBorrowAprOverride: persisted?.manualBorrowAprOverride ?? null,
  welcomed: persisted?.welcomed ?? false,
  // Patch BZ: hydrate checklist done flags from persisted state. Defaults
  // to {} so a fresh account starts with everything unticked.
  securityChecklist: (persisted as { securityChecklist?: Record<string, boolean> } | undefined)?.securityChecklist ?? {},

  view: 'dashboard',
  liveStatus: 'idle',
  liveError: null,
  aaveStatus: 'idle',
  aaveError: null,
  etfPriceStatus: 'idle',
  etfPriceError: null,
  // Patch AS: session-only; never persisted. Initialized to null because
  // a fresh page load is never mid-view-as (the snapshot key in localStorage
  // is cruft from a prior session if anything; safe to ignore until the
  // admin clicks View again).
  viewingAs: null,

  undoStack: [],

  setView: v => set({ view: v }),

  // ---- purchases ----
  addPurchase: p => set(s => {
    const id = `tx-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const tx: PurchaseTransaction = { ...p, id };
    const next = withUndo(s, 'purchase:add', {
      purchases: [...s.purchases, tx].sort((a, b) => a.date.getTime() - b.date.getTime()),
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  updatePurchase: (id, patch) => set(s => {
    // Patch BB: stripping autoCreated on user edits. Walker creates rows
    // with autoCreated: true to flag them with the AUTO badge. The moment
    // the user touches ANY field (USD, BTC, date, note, loan), the row
    // graduates to manual: autoCreated is forced false here, regardless of
    // what the caller passed. False (not undefined) so JSON-round-tripped
    // rows have an explicit value rather than relying on absent-key
    // semantics for the check `p.autoCreated === true`.
    const next = withUndo(s, `purchase:${id}`, {
      purchases: s.purchases.map(p => (p.id === id ? { ...p, ...patch, autoCreated: false } : p))
        .sort((a, b) => a.date.getTime() - b.date.getTime()),
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  removePurchase: id => set(s => {
    const next = withUndo(s, 'purchase:remove', {
      purchases: s.purchases.filter(p => p.id !== id),
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  // ---- goals ----
  addGoal: g => set(s => {
    const id = `goal-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const newGoal: Goal = { ...g, id };
    const next = withUndo(s, 'goal:add', {
      goals: [...s.goals, newGoal],
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  updateGoal: (id, patch) => set(s => {
    const next = withUndo(s, `goal:${id}`, {
      goals: s.goals.map(g => g.id === id ? { ...g, ...patch } : g),
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  removeGoal: id => set(s => {
    const next = withUndo(s, 'goal:remove', {
      goals: s.goals.filter(g => g.id !== id),
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  // ---- live data (no undo: background fetches, not user actions;
  //                  no cloud sync: transient, re-fetched on every load) ----
  //
  // Patch DU: setLiveSnapshot writes to localStorage but does NOT call
  // saveToStorage, because saveToStorage routes through
  // scheduleCloudSaveIfSignedIn and would fire a cloud save every time
  // the BTC price refresh interval ticks (every 2 minutes via Dashboard
  // useAutoRefresh). That triggered the 'saving' indicator on every
  // refresh for data the user never edited, and worse, when a save got
  // stuck (rate-limited save window, network flake) the UI was stuck on
  // 'saving' even though no actual user state was at risk. The other
  // live-data setters (setAaveSnapshot, setLednSnapshot, setLiveStatus,
  // setAaveStatus, setLednStatus, setEtfPriceStatus) all correctly use a
  // bare set() with no save path - setLiveSnapshot was the outlier and
  // the source of the spurious 'saving' that users were reporting on tab
  // switches and other non-edit interactions. Local storage is still
  // updated directly here so a cold boot can still flash the last known
  // price while the fresh fetch runs. Incidental cloud capture still
  // happens whenever the user makes a real edit (encodeStateForPersist
  // includes liveSnapshot for opportunistic capture during legitimate
  // saves), so cross-device price display works without burning a
  // dedicated save cycle every two minutes.
  setLiveSnapshot: snapshot => set(s => {
    const next = { liveSnapshot: snapshot };
    try {
      const merged = { ...s, ...next };
      const payload = encodeStateForPersist(merged);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {
      console.warn('Failed to persist liveSnapshot to localStorage', e);
    }
    return next;
  }),

  setLiveStatus: (status, error = null) => set({ liveStatus: status, liveError: error }),

  // ---- nav / UI prefs (no undo: not edits) ----
  selectScenario: id => set(s => {
    saveToStorage({ ...s, selectedScenarioId: id });
    return { selectedScenarioId: id };
  }),

  setPaceMethod: m => set(s => {
    saveToStorage({ ...s, paceMethod: m });
    return { paceMethod: m };
  }),

  setFuturePlanWindowYears: n => set(s => {
    saveToStorage({ ...s, futurePlanWindowYears: n });
    return { futurePlanWindowYears: n };
  }),

  // ---- scenarios ----
  createScenario: (name, sourceScenarioId) => {
    const id = `custom-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const source = sourceScenarioId ? get().scenarios.find(s => s.id === sourceScenarioId) : undefined;
    const newScenario: ScenarioPlan = {
      id,
      name,
      description: source ? `Forked from ${source.name}` : 'Custom scenario.',
      source: 'custom',
      events: source ? source.events.map(e => ({ ...e })) : [],
    };
    set(s => {
      const next = withUndo(s, 'scenario:add', {
        scenarios: [...s.scenarios, newScenario],
        selectedScenarioId: id,
      });
      saveToStorage({ ...s, ...next });
      return next;
    });
    return id;
  },

  updateScenario: (id, patch) => set(s => {
    const next = withUndo(s, `scenario:${id}`, {
      scenarios: s.scenarios.map(sc =>
        sc.id === id && sc.source === 'custom' ? { ...sc, ...patch } : sc,
      ),
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  deleteScenario: id => set(s => {
    const target = s.scenarios.find(sc => sc.id === id);
    if (!target || target.source === 'seed') return s;
    const remaining = s.scenarios.filter(sc => sc.id !== id);
    const next = withUndo(s, 'scenario:remove', {
      scenarios: remaining,
      selectedScenarioId: s.selectedScenarioId === id
        ? (remaining[0]?.id ?? null)
        : s.selectedScenarioId,
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  upsertScenarioEvent: (scenarioId, index, event) => set(s => {
    const next = withUndo(s, `scenario-event:${scenarioId}`, {
      scenarios: s.scenarios.map(sc => {
        if (sc.id !== scenarioId) return sc;
        let nextEvents: PlannedEvent[];
        if (index === null) {
          nextEvents = [...sc.events, event];
        } else {
          nextEvents = [...sc.events];
          nextEvents[index] = event;
        }
        nextEvents.sort((a, b) => a.date.getTime() - b.date.getTime());
        return { ...sc, events: nextEvents };
      }),
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  removeScenarioEvent: (scenarioId, index) => set(s => {
    const next = withUndo(s, `scenario-event:${scenarioId}:remove`, {
      scenarios: s.scenarios.map(sc => {
        if (sc.id !== scenarioId) return sc;
        return { ...sc, events: sc.events.filter((_, i) => i !== index) };
      }),
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  appendSalaryToScenario: (scenarioId, salary, fromDate, toDate) => set(s => {
    const newEvents = expandSalary(salary, fromDate, toDate);
    const next = withUndo(s, `scenario-event:${scenarioId}:append-salary`, {
      scenarios: s.scenarios.map(sc => {
        if (sc.id !== scenarioId) return sc;
        const merged = [...sc.events, ...newEvents]
          .sort((a, b) => a.date.getTime() - b.date.getTime());
        return { ...sc, events: merged };
      }),
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  // ---- loop loan ----
  updateLoopLoanInputs: patch => set(s => {
    // Whole-card coalesce: any change to any input field within 1s coalesces
    // into one undo step. Matches user's mental model "I was tweaking the
    // loop loan inputs" rather than "I typed each digit separately".
    const next = withUndo(s, 'loop-loan:inputs', {
      loopLoanInputs: { ...s.loopLoanInputs, ...patch },
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  setAaveSnapshot: snapshot => set({ aaveSnapshot: snapshot }),
  setAaveStatus: (status, error = null) => set({ aaveStatus: status, aaveError: error }),

  // Patch BE: provider selection. Persisted, undo-tracked - changing
  // provider is a user-meaningful action that should be reversible.
  setLoanProvider: provider => set(s => {
    const next = withUndo(s, 'loan-provider:set', { selectedLoanProvider: provider });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  // Patch BE: Ledn snapshot setter. Same shape as setAaveSnapshot.
  // NOT persisted - rebuilt from io/live/ledn.ts on each boot.
  setLednSnapshot: snapshot => set({ lednSnapshot: snapshot }),
  setLednStatus: (status, error = null) => set({ lednStatus: status, lednError: error }),

  // Patch BE: manual borrow APR override. Setting clears via passing null.
  // Persisted (a user-entered value). Undo-tracked.
  setManualBorrowAprOverride: apr => set(s => {
    const next = withUndo(s, 'manual-borrow-apr:set', { manualBorrowAprOverride: apr });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  setDefillamaQuery: q => set(s => {
    const next = withUndo(s, 'defillama:query', { defillamaQuery: q });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  // ---- carry retirement ----
  updateCarryInputs: patch => set(s => {
    const next = withUndo(s, 'carry:inputs', {
      carryInputs: { ...s.carryInputs, ...patch },
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  // ---- seed / IO. Each gets its own snapshot; these are big resets the
  //      user might absolutely want to undo. ----
  loadFromSeed: async () => {
    const seed = await loadSeed();
    const purchases = seedActualsToTransactions(seed);
    const goals = seedGoalsToGoals(seed);
    const scenarios = seedToScenarios(seed);
    const loopLoanInputs = seedToLoopLoanInputs(seed);
    const carryInputs = seedToCarryInputs(seed);
    // Patch AI: also reload Paul's ETF holdings/transactions on seed-load
    // so "Reset to seed" is a real reset across the board.
    const etfHoldings = paulEtfHoldings();
    const etfTransactions = paulEtfTransactions();
    // Patch AJ: also reload Paul's Bain consulting ladder. Pre-AJ the salary
    // plan was the one slice loadFromSeed left alone; now reset-to-seed
    // restores it too, parallel to the ETF treatment.
    const salaryPlan = bainSalaryPlan();
    set(s => {
      const next = withUndo(s, 'seed:load', {
        purchases,
        goals,
        scenarios,
        selectedScenarioId: scenarios[0]?.id ?? null,
        loopLoanInputs,
        carryInputs,
        etfHoldings,
        etfTransactions,
        salaryPlan,
        lastSeedLoadedAt: new Date(),
      });
      saveToStorage({ ...s, ...next });
      return next;
    });
  },

  restoreScenarioFromSeed: async (scenarioId: string) => {
    const seed = await loadSeed();
    const fresh = seedToScenarios(seed).find(sc => sc.id === scenarioId);
    if (!fresh) return;
    set(s => {
      const next = withUndo(s, `seed:restore-scenario:${scenarioId}`, {
        scenarios: s.scenarios.map(sc => sc.id === scenarioId ? fresh : sc),
      });
      saveToStorage({ ...s, ...next });
      return next;
    });
  },

  resetToSeed: async () => {
    localStorage.removeItem(STORAGE_KEY);
    await get().loadFromSeed();
    // Patch AP: force the seed-load to propagate to cloud now, not 1500ms
    // from now via the debounced path. Critical because this is the only
    // affordance the user has to recover from a wiped cloud row, and the
    // debounced save can be cancelled by a setLiveSnapshot firing in the
    // same second (live data hooks poll every ~30s and one can easily
    // land in the debounce window). Pre-AP, that exact race meant Paul's
    // reset-to-seed worked locally but never reached cloud, so the next
    // refresh re-pulled the wiped row and the recovery silently undid
    // itself.
    const s = get();
    const payload = encodeStateForPersist(s);
    await saveImmediatelyIfSignedIn(payload);
  },

  exportToJson: () => {
    const s = get();
    downloadExport({
      purchases: s.purchases,
      goals: s.goals,
      liveSnapshot: s.liveSnapshot,
      lastSeedLoadedAt: s.lastSeedLoadedAt,
      scenarios: s.scenarios,
      selectedScenarioId: s.selectedScenarioId,
      paceMethod: s.paceMethod,
      loopLoanInputs: s.loopLoanInputs,
      defillamaQuery: s.defillamaQuery,
      carryInputs: s.carryInputs,
      etfHoldings: s.etfHoldings,
      etfTransactions: s.etfTransactions,
      priceModel: s.priceModel,
    });
  },

  importFromJson: (text: string) => {
    // Patch AP: lenient parsing. Two formats reach this function:
    //   (1) in-app exportToJson outputs, wrapped {app:"btc-stack-planner",
    //       version, state:{...}} with already-decoded Decimals/Dates.
    //   (2) Sunday Resend backup emails, which carry the raw
    //       app_state.state jsonb column (no envelope, "dec:"/"dt:"
    //       prefixes intact, same shape as localStorage).
    // Try the envelope path first (parseImport); on its "Not a
    // btc-stack-planner export" failure, fall back to decodePersistedState
    // on the parsed JSON. The fallback reuses the exact decoder that
    // applyPersistedState uses, so a backup row imports identically to
    // how it would have loaded from cloud.
    let partial: Partial<AppState>;
    try {
      const parsed = parseImport(text);
      const v = parsed.state;
      partial = {};
      if (Array.isArray(v.purchases)) partial.purchases = v.purchases as PurchaseTransaction[];
      if (Array.isArray(v.goals)) partial.goals = v.goals as Goal[];
      if (v.liveSnapshot && typeof v.liveSnapshot === 'object') {
        partial.liveSnapshot = v.liveSnapshot as LiveBtcSnapshot;
      }
      if (v.lastSeedLoadedAt instanceof Date) partial.lastSeedLoadedAt = v.lastSeedLoadedAt;
      if (Array.isArray(v.scenarios)) partial.scenarios = v.scenarios as ScenarioPlan[];
      if (typeof v.selectedScenarioId === 'string') partial.selectedScenarioId = v.selectedScenarioId;
      if (v.paceMethod === 'allTimePace' || v.paceMethod === 'recentPace') {
        partial.paceMethod = v.paceMethod;
      }
      if (v.loopLoanInputs && typeof v.loopLoanInputs === 'object') {
        // Patch AL: same defaults-merge logic as decodePersistedState. An
        // imported JSON from a pre-AL export won't have bullRallyInProjection;
        // we fill the gap with the default rather than letting it be undefined.
        partial.loopLoanInputs = {
          ...defaultLoopLoanInputs(),
          ...(v.loopLoanInputs as Partial<LoopLoanInputs>),
        };
      }
      if (v.defillamaQuery && typeof v.defillamaQuery === 'object') {
        partial.defillamaQuery = v.defillamaQuery as DefiLlamaQuery;
      }
      if (v.carryInputs && typeof v.carryInputs === 'object') {
        partial.carryInputs = v.carryInputs as CarryInputs;
      }
      if (Array.isArray(v.etfHoldings)) partial.etfHoldings = v.etfHoldings as EtfHolding[];
      if (Array.isArray(v.etfTransactions)) partial.etfTransactions = v.etfTransactions as EtfTransaction[];
      if (v.priceModel && typeof v.priceModel === 'object') {
        // Patch BS: skip apply if pre-BS shape (see migration note in
        // primary decode path above).
        const pm = v.priceModel as Record<string, unknown>;
        const isOldShape = 'initialDoublingPeriodDays' in pm || 'increaseInTimeToDouble' in pm;
        if (!isOldShape) {
          partial.priceModel = v.priceModel as BtcPriceModel;
        }
      }
    } catch (envelopeErr) {
      // Patch AP: envelope path failed (most likely "Not a
      // btc-stack-planner export"). Try interpreting `text` as a raw
      // app_state.state column. If JSON.parse fails OR no recognized
      // fields decode out, surface the ORIGINAL envelope error since
      // that's the format users were probably aiming for; the fallback
      // is a quiet rescue, not a replacement.
      let rawParsed: unknown;
      try {
        rawParsed = JSON.parse(text);
      } catch {
        throw envelopeErr;
      }
      const decoded = decodePersistedState(rawParsed);
      if (Object.keys(decoded).length === 0) throw envelopeErr;
      partial = decoded;
    }
    set(s => {
      const next = withUndo(s, 'import:json', partial);
      saveToStorage({ ...s, ...next });
      return next;
    });
    // Patch AP: force immediate cloud propagation, same rationale as
    // resetToSeed. The saveToStorage above queues a debounced save that
    // can be cancelled by a setLiveSnapshot landing within 1500ms; the
    // immediate path bypasses the timer and writes synchronously.
    const after = get();
    const payload = encodeStateForPersist(after);
    void saveImmediatelyIfSignedIn(payload);
  },

  // ---- ETF holdings ----
  addEtfHolding: (holding) => {
    const id = `etf-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    set(s => {
      const next = withUndo(s, 'etf-holding:add', {
        etfHoldings: [...s.etfHoldings, { ...holding, id }],
      });
      saveToStorage({ ...s, ...next });
      return next;
    });
    // Patch BB: auto-fetch live price for the new holding. Previously
    // adding an ETF left livePriceSnapshot null until the user navigated
    // to Dashboard and clicked refresh, which made the new row read
    // "no live price" everywhere it was referenced (NetWorth, EtfHoldings,
    // EtfTransactions). Now we fire-and-forget a price fetch immediately
    // after the holding lands in state. Failures (network down, symbol
    // not found, etc.) leave the holding in the pre-BB state (no price)
    // and surface via etfPriceStatus + etfPriceError; the user can still
    // click refresh manually as a fallback.
    //
    // Dynamic import keeps store.ts decoupled from the io/live layer at
    // bundle time (only loaded the first time someone adds a holding,
    // and on Dashboard's refresh which already statically imports it).
    void (async () => {
      try {
        useAppStore.getState().setEtfPriceStatus('loading');
        const mod = await import('../io/live/etf-price.js');
        const snap = await mod.fetchEtfPrice(holding.symbol);
        useAppStore.getState().setEtfLivePrice(id, snap);
        useAppStore.getState().setEtfPriceStatus('ok');
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        console.warn(`[addEtfHolding] auto price fetch failed for ${holding.symbol}:`, msg);
        useAppStore.getState().setEtfPriceStatus('error', msg);
      }
    })();
    return id;
  },

  updateEtfHolding: (id, patch) => set(s => {
    const next = withUndo(s, `etf-holding:${id}`, {
      etfHoldings: s.etfHoldings.map(h => h.id === id ? { ...h, ...patch } : h),
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  // Patch DX: dedicated live-price setter, bypasses saveToStorage and
  // withUndo. Pre-DX this was a regular updateEtfHolding(id,
  // { livePriceSnapshot: snap }) call which routed through both - the
  // save fired a cloud write every 2 minutes (Dashboard interval) and
  // every tab-visibility regain, the undo wrap pushed a stack entry for
  // each one. Both were noise for transient background-fetched data.
  setEtfLivePrice: (id, snap) => set(s => ({
    etfHoldings: s.etfHoldings.map(h =>
      h.id === id
        ? { ...h, livePriceSnapshot: snap ?? undefined }
        : h,
    ),
  })),

  removeEtfHolding: id => set(s => {
    const next = withUndo(s, 'etf-holding:remove', {
      etfHoldings: s.etfHoldings.filter(h => h.id !== id),
      etfTransactions: s.etfTransactions.filter(t => t.holdingId !== id),
      // Patch BB: also drop any auto-buy schedule for this holding so we
      // don't leave an orphan schedule pointing at a deleted asset. The
      // walker would skip it anyway (priceMap.get returns undefined for
      // an unknown id), but the orphan would clutter localStorage and
      // confuse any future tooling that reads autoBuySchedules directly.
      autoBuySchedules: s.autoBuySchedules.filter(
        sch => !(sch.assetType === 'etf' && sch.assetId === id),
      ),
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  addEtfTransaction: (tx) => set(s => {
    const id = `etx-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const newTx = { ...tx, id };
    const next = withUndo(s, 'etf-tx:add', {
      etfTransactions: [...s.etfTransactions, newTx]
        .sort((a, b) => a.date.getTime() - b.date.getTime()),
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  updateEtfTransaction: (id, patch) => set(s => {
    // Patch BB: same auto-strip pattern as updatePurchase. Any user edit
    // graduates a walker-created row to manual. The AUTO badge in the
    // table watches `t.autoCreated === true`, so setting to false hides it.
    const next = withUndo(s, `etf-tx:${id}`, {
      etfTransactions: s.etfTransactions.map(t => t.id === id ? { ...t, ...patch, autoCreated: false } : t)
        .sort((a, b) => a.date.getTime() - b.date.getTime()),
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  removeEtfTransaction: id => set(s => {
    const next = withUndo(s, 'etf-tx:remove', {
      etfTransactions: s.etfTransactions.filter(t => t.id !== id),
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  setEtfPriceStatus: (status, error = null) => set({ etfPriceStatus: status, etfPriceError: error }),

  // ---- security CRUD (Patch BJ) ----
  // Patterns mirror the ETF actions exactly. addSecurityAsset triggers a
  // background price fetch via the lazy-import pattern so the row gets
  // populated without the user clicking refresh. Transactions are simple
  // CRUD; running balance is derived at render time, not stored.

  addSecurityAsset: (asset) => {
    const id = `sec_${Math.random().toString(36).slice(2, 10)}`;
    set(s => {
      const next = withUndo(s, 'security:add', {
        securityAssets: [...s.securityAssets, { ...asset, id }],
      });
      saveToStorage({ ...s, ...next });
      return next;
    });
    // Patch BJ: kick off a background price fetch immediately after add
    // so the new row populates without the user needing to refresh. Lazy
    // import keeps the price provider off the initial bundle path.
    void (async () => {
      try {
        const { fetchEtfPrice } = await import('../io/live/etf-price.js');
        const snap = await fetchEtfPrice(asset.symbol);
        useAppStore.getState().setSecurityLivePrice(id, {
          priceUsd: snap.priceUsd, fetchedAt: new Date(),
        });
      } catch (e) {
        console.warn(`Security price fetch failed for ${asset.symbol}:`, e);
      }
    })();
    return id;
  },

  updateSecurityAsset: (id, patch) => set(s => {
    const next = withUndo(s, 'security:update', {
      securityAssets: s.securityAssets.map(a => a.id === id ? { ...a, ...patch } : a),
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  // Patch DX: dedicated live-price setter for securities, same shape as
  // setEtfLivePrice. Bypasses save and undo for background-fetched
  // transient data.
  setSecurityLivePrice: (id, snap) => set(s => ({
    securityAssets: s.securityAssets.map(a =>
      a.id === id
        ? { ...a, livePriceSnapshot: snap ?? undefined }
        : a,
    ),
  })),

  removeSecurityAsset: id => set(s => {
    const next = withUndo(s, 'security:remove', {
      // Cascade-remove this asset's transactions too. Same pattern
      // removeEtfHolding uses; orphan transactions serve no purpose
      // and would just be dead rows.
      securityAssets: s.securityAssets.filter(a => a.id !== id),
      securityTransactions: s.securityTransactions.filter(t => t.assetId !== id),
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  addSecurityTransaction: (tx) => set(s => {
    const id = `sectx_${Math.random().toString(36).slice(2, 10)}`;
    const next = withUndo(s, 'security-tx:add', {
      securityTransactions: [...s.securityTransactions, { ...tx, id }],
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  updateSecurityTransaction: (id, patch) => set(s => {
    // Any user-initiated edit clears the autoCreated badge, same as the
    // ETF / Purchases pattern. The walker will skip rows where
    // autoCreated is unset, so this is reversible without rebuilding.
    const next = withUndo(s, 'security-tx:update', {
      securityTransactions: s.securityTransactions.map(t => t.id === id
        ? { ...t, ...patch, autoCreated: false }
        : t),
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  removeSecurityTransaction: id => set(s => {
    const next = withUndo(s, 'security-tx:remove', {
      securityTransactions: s.securityTransactions.filter(t => t.id !== id),
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  // ---- real estate / cash / debt CRUD (Patch BL) ----
  // Three list-of-items shapes. No background workers, no derived
  // schedules: balances are recomputed at render time (mortgage
  // amortization for real estate, simple sums for cash and debt).

  addRealEstateProperty: (p) => {
    const id = `re_${Math.random().toString(36).slice(2, 10)}`;
    set(s => {
      const next = withUndo(s, 'real-estate:add', {
        realEstateProperties: [...s.realEstateProperties, { ...p, id }],
      });
      saveToStorage({ ...s, ...next });
      return next;
    });
    return id;
  },

  updateRealEstateProperty: (id, patch) => set(s => {
    const next = withUndo(s, 'real-estate:update', {
      realEstateProperties: s.realEstateProperties.map(p => p.id === id ? { ...p, ...patch } : p),
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  removeRealEstateProperty: id => set(s => {
    const next = withUndo(s, 'real-estate:remove', {
      realEstateProperties: s.realEstateProperties.filter(p => p.id !== id),
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  addCashEntry: (c) => {
    const id = `cash_${Math.random().toString(36).slice(2, 10)}`;
    set(s => {
      const next = withUndo(s, 'cash:add', {
        cashEntries: [...s.cashEntries, { ...c, id }],
      });
      saveToStorage({ ...s, ...next });
      return next;
    });
    return id;
  },

  updateCashEntry: (id, patch) => set(s => {
    const next = withUndo(s, 'cash:update', {
      cashEntries: s.cashEntries.map(c => c.id === id ? { ...c, ...patch } : c),
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  removeCashEntry: id => set(s => {
    const next = withUndo(s, 'cash:remove', {
      cashEntries: s.cashEntries.filter(c => c.id !== id),
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  addDebtItem: (d) => {
    const id = `debt_${Math.random().toString(36).slice(2, 10)}`;
    set(s => {
      const next = withUndo(s, 'debt:add', {
        debtItems: [...s.debtItems, { ...d, id }],
      });
      saveToStorage({ ...s, ...next });
      return next;
    });
    return id;
  },

  updateDebtItem: (id, patch) => set(s => {
    const next = withUndo(s, 'debt:update', {
      debtItems: s.debtItems.map(d => d.id === id ? { ...d, ...patch } : d),
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  removeDebtItem: id => set(s => {
    const next = withUndo(s, 'debt:remove', {
      debtItems: s.debtItems.filter(d => d.id !== id),
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  // ---- price model ----
  updatePriceModel: (patch) => set(s => {
    const next = withUndo(s, 'price-model:update', {
      priceModel: { ...s.priceModel, ...patch },
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  resetPriceModel: () => set(s => {
    const next = withUndo(s, 'price-model:reset', {
      priceModel: WORKBOOK_PRICE_MODEL,
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  // ---- loan events ----
  addLoanEvent: (e) => set(s => {
    const newEvent: LoanEvent = { ...e, id: crypto.randomUUID() };
    const next = withUndo(s, 'loan-event:add', {
      loanEvents: [...s.loanEvents, newEvent],
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  updateLoanEvent: (id, patch) => set(s => {
    const next = withUndo(s, `loan-event:${id}`, {
      loanEvents: s.loanEvents.map(e => e.id === id ? { ...e, ...patch } : e),
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  removeLoanEvent: (id) => set(s => {
    const next = withUndo(s, 'loan-event:remove', {
      loanEvents: s.loanEvents.filter(e => e.id !== id),
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  // ---- salary plan ----
  updateSalaryPlan: (patch) => set(s => {
    const next = withUndo(s, 'salary:plan', {
      salaryPlan: { ...s.salaryPlan, ...patch },
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  updateSalaryLevel: (id, patch) => set(s => {
    const next = withUndo(s, `salary-level:${id}`, {
      salaryPlan: {
        ...s.salaryPlan,
        levels: s.salaryPlan.levels.map(l => l.id === id ? { ...l, ...patch } : l),
      },
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  reorderSalaryLevels: (idsInOrder) => set(s => {
    const lookup = new Map(s.salaryPlan.levels.map(l => [l.id, l]));
    const reordered = idsInOrder
      .map(id => lookup.get(id))
      .filter((l): l is import('../engine/types.js').SalaryLevel => !!l);
    if (reordered.length !== s.salaryPlan.levels.length) {
      return {};
    }
    const recomputed = reordered.map((l, i, arr) => {
      if (i === 0) return l;
      let months = 0;
      for (let j = 1; j < i; j++) {
        const dur = arr[j - 1]!.durationYears
          ? arr[j - 1]!.durationYears!.toNumber()
          : 1;
        months += Math.round(dur * 12);
      }
      const newDate = new Date(arr[0]!.promotedAt);
      newDate.setUTCMonth(newDate.getUTCMonth() + months);
      return { ...l, promotedAt: newDate };
    });
    const next = withUndo(s, 'salary:reorder', {
      salaryPlan: { ...s.salaryPlan, levels: recomputed },
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  resetSalaryPlan: () => set(s => {
    const next = withUndo(s, 'salary:reset', {
      salaryPlan: defaultSalaryPlan(),
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  setSalaryBreak: (afterLevelIndex, breakStart, breakEnd) => set(s => {
    const next = withUndo(s, 'salary:break', {
      salaryPlan: {
        ...s.salaryPlan,
        career_break: { afterLevelIndex, breakStart, breakEnd },
      },
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  clearSalaryBreak: () => set(s => {
    const next = withUndo(s, 'salary:break-clear', {
      salaryPlan: { ...s.salaryPlan, career_break: undefined },
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  // ---- Patch AW: custom buys ----
  // The three actions mirror the addEtfHolding / addLoanEvent shape: each
  // mutation is wrapped in withUndo (so the user can roll back a deletion
  // or accidental edit) and routed through saveToStorage (which fans out
  // to localStorage and the debounced cloud-sync). customBuys lives on
  // salaryPlan as an optional field; pre-AW plans load with customBuys
  // === undefined, so every read uses `?? []` to coerce.
  //
  // Id format: `custom-${timestamp}-${random}`. Same pattern existing
  // actions use; collisions are vanishingly improbable for one user but
  // the random suffix protects against rapid double-clicks issuing two
  // adds in the same millisecond.

  addCustomBuy: (partial) => {
    const id = `custom-${Date.now()}-${Math.floor(Math.random() * 100000)}`;
    set(s => {
      const buy: CustomBuy = {
        id,
        kind: 'lump',
        startDate: new Date(),
        totalUsd: ZERO,
        ...partial,
      };
      const existing = s.salaryPlan.customBuys ?? [];
      const next = withUndo(s, 'custom-buy:add', {
        salaryPlan: { ...s.salaryPlan, customBuys: [...existing, buy] },
      });
      saveToStorage({ ...s, ...next });
      return next;
    });
    return id;
  },

  updateCustomBuy: (id, patch) => set(s => {
    const existing = s.salaryPlan.customBuys ?? [];
    const updated = existing.map(b => b.id === id ? { ...b, ...patch } : b);
    const next = withUndo(s, `custom-buy:update:${id}`, {
      salaryPlan: { ...s.salaryPlan, customBuys: updated },
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  removeCustomBuy: (id) => set(s => {
    const existing = s.salaryPlan.customBuys ?? [];
    const filtered = existing.filter(b => b.id !== id);
    const next = withUndo(s, `custom-buy:remove:${id}`, {
      salaryPlan: { ...s.salaryPlan, customBuys: filtered },
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  // ---- Patch BB: auto-buy schedule actions ----
  //
  // Three actions: upsert (create or patch), remove (drop schedule), and
  // runAutoBuyWalker (engine call that creates real transaction rows for
  // scheduled dates that have passed). The walker is invoked by App.tsx
  // when liveSnapshot is available; it's idempotent (re-running with no
  // new scheduled dates is a no-op), which means we can call it on every
  // price refresh without worrying about duplicates.
  //
  // The schedule constraint "at most one per (assetType, assetId)" is
  // enforced by upsertAutoBuy: it finds an existing schedule by
  // (assetType, assetId) and patches it if present, creates one if not.
  // Callers don't pass schedule IDs; the lookup key is the asset.

  upsertAutoBuy: (assetType, assetId, patch) => set(s => {
    const existing = s.autoBuySchedules.find(
      sch => sch.assetType === assetType && sch.assetId === assetId,
    );

    let schedules: AutoBuySchedule[];
    if (existing) {
      // Patch BB nuance: toggling enabled from false to true on an
      // existing schedule resets lastPopulatedDate to NOW so the user
      // doesn't get a surprise backfill of all the dates that passed
      // while disabled. We only do this for the false->true transition;
      // an explicit caller-supplied lastPopulatedDate in the patch wins
      // (used by the walker to advance lastPopulatedDate normally).
      let effectivePatch = { ...patch };
      const enablingNow = patch.enabled === true && existing.enabled === false;
      if (enablingNow && patch.lastPopulatedDate === undefined) {
        // Patch BD: normalize to local-noon UTC so the next walker run
        // compares like-to-like with other normalized dates (startDate
        // defaults, walker today). Without this, re-enabling near midnight
        // could leave lastPopulatedDate on the wrong calendar day.
        effectivePatch.lastPopulatedDate = todayLocalNoonUtc();
      }
      schedules = s.autoBuySchedules.map(sch =>
        sch === existing ? { ...sch, ...effectivePatch } : sch,
      );
    } else {
      // Create a new schedule with sensible defaults merged with the
      // caller's patch. The id is fresh; assetType/assetId locked to
      // the caller's params so they can't accidentally drift.
      const newSchedule: AutoBuySchedule = {
        id: `ab-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        assetType,
        assetId,
        enabled: false,
        cadence: 'monthly',
        amountUsd: ZERO,
        // Patch BD: noon-UTC of the user's local date, not the current
        // UTC moment. Prevents an evening-EDT enable from defaulting
        // startDate to tomorrow's UTC date.
        startDate: todayLocalNoonUtc(),
        ...patch,
      };
      schedules = [...s.autoBuySchedules, newSchedule];
    }

    const next = withUndo(s, `auto-buy:${assetType}:${assetId}`, {
      autoBuySchedules: schedules,
    });
    const merged = { ...s, ...next };
    saveToStorage(merged);
    // Patch BE: auto-buy edits bypass the 1500ms cloud-save debounce.
    // Pre-BE bug: typing a schedule and refreshing within 1.5s lost the
    // edit because AuthGate.resetToDefaults wipes local state on every
    // sign-in and then overlays the (still-stale) cloud row. Forcing an
    // immediate cloud write closes that race for the auto-buy slice.
    // Same pattern resetToSeed and importFromJson use (Patch AP).
    void saveImmediatelyIfSignedIn(encodeStateForPersist(merged));
    return next;
  }),

  removeAutoBuy: (assetType, assetId) => set(s => {
    const filtered = s.autoBuySchedules.filter(
      sch => !(sch.assetType === assetType && sch.assetId === assetId),
    );
    // Skip the undo push if nothing changed (caller hit Remove on an
    // asset with no schedule; not an error, just a no-op).
    if (filtered.length === s.autoBuySchedules.length) return {};
    const next = withUndo(s, `auto-buy:remove:${assetType}:${assetId}`, {
      autoBuySchedules: filtered,
    });
    const merged = { ...s, ...next };
    saveToStorage(merged);
    // Patch BE: bypass debounce on removal too. Same rationale as the
    // upsert path above - protects against the refresh-within-1.5s
    // cloud race.
    void saveImmediatelyIfSignedIn(encodeStateForPersist(merged));
    return next;
  }),

  runAutoBuyWalker: () => set(s => {
    // Build the price map. BTC keyed by literal 'btc' (matching the
    // schedule.assetId convention); each ETF holding keyed by its id.
    // ETF prices prefer manual override (manualPriceUsd) over live
    // snapshot, same precedence rule the rest of the app uses for
    // display (manual is "what the user explicitly told us"; live is
    // "best automated guess").
    const priceMap = new Map<string, Decimal>();
    if (s.liveSnapshot && s.liveSnapshot.priceUsd.greaterThan(0)) {
      priceMap.set('btc', s.liveSnapshot.priceUsd);
    }
    for (const h of s.etfHoldings) {
      const price = h.manualPriceUsd ?? h.livePriceSnapshot?.priceUsd ?? null;
      if (price && price.greaterThan(0)) priceMap.set(h.id, price);
    }

    // Engine call. Pure function; returns the batch to apply.
    // Patch BD: normalize "today" to local-noon UTC so the walker's
    // boundary comparison (`scheduledDate <= today`) is a same-calendar-
    // day comparison, not a moment comparison. Without this, near
    // midnight UTC the walker could emit "tomorrow's" row from the
    // user's perspective (the bug Paul saw on May 10 evening EDT).
    const result = walkAutoBuySchedules(s.autoBuySchedules, priceMap, todayLocalNoonUtc());

    // Nothing to do? Bail before pushing an empty undo step.
    if (result.newPurchases.length === 0
      && result.newEtfTransactions.length === 0
      && result.scheduleUpdates.length === 0) {
      return {};
    }

    // Assign IDs to the new rows. Use a monotonically-incrementing
    // index in the suffix so two rows created on the same millisecond
    // can't collide on Date.now()+random alone.
    const baseTime = Date.now();
    let counter = 0;
    const newPurchasesWithIds: PurchaseTransaction[] = result.newPurchases.map(p => {
      const id = `tx-${baseTime}-${(counter++).toString(36)}${Math.random().toString(36).slice(2, 4)}`;
      return { ...p, id };
    });
    const newEtfTxsWithIds: EtfTransaction[] = result.newEtfTransactions.map(t => {
      const id = `etx-${baseTime}-${(counter++).toString(36)}${Math.random().toString(36).slice(2, 4)}`;
      return { ...t, id };
    });

    const next = withUndo(s, 'auto-buy:walker', {
      purchases: [...s.purchases, ...newPurchasesWithIds]
        .sort((a, b) => a.date.getTime() - b.date.getTime()),
      etfTransactions: [...s.etfTransactions, ...newEtfTxsWithIds]
        .sort((a, b) => a.date.getTime() - b.date.getTime()),
      autoBuySchedules: s.autoBuySchedules.map(sch => {
        const update = result.scheduleUpdates.find(u => u.id === sch.id);
        return update ? { ...sch, lastPopulatedDate: update.lastPopulatedDate } : sch;
      }),
    });
    saveToStorage({ ...s, ...next });
    return next;
  }),

  // ---- Patch AJ: welcome-toast dismissal ----
  // No undo wrapping. The welcome flag isn't a content edit, it's a one-shot
  // UI state, and rolling back into "un-welcomed" via undo would re-show the
  // toast which would feel broken. Direct set + saveToStorage is the right
  // shape so the dismissal still persists across reloads and devices.
  setWelcomed: (v) => set(s => {
    const next = { welcomed: v };
    saveToStorage({ ...s, ...next });
    return next;
  }),

  toggleSecurityChecklistItem: (id) => set(s => {
    const prev = s.securityChecklist[id] ?? false;
    const nextMap = { ...s.securityChecklist, [id]: !prev };
    saveToStorage({ ...s, securityChecklist: nextMap });
    return { securityChecklist: nextMap };
  }),

  // ---- Patch O: undo action ----
  undo: () => set(s => {
    if (s.undoStack.length === 0) return {};
    const lastSnapshot = s.undoStack[s.undoStack.length - 1]!;
    const newStack = s.undoStack.slice(0, -1);

    try {
      const parsed = JSON.parse(lastSnapshot);
      const decoded = decodePersistedState(parsed);
      // Reset coalesce tracking so the next mutation pushes a fresh
      // snapshot instead of trying to coalesce against a key that no
      // longer represents anything in the undo stack.
      lastSnapshotKey = '';
      lastSnapshotTime = 0;
      const next: Partial<AppState> = {
        ...decoded,
        undoStack: newStack,
      };
      // Mirror the restored state to localStorage. The cloud save runs
      // through scheduleCloudSaveIfSignedIn inside saveToStorage, so the
      // undo also propagates to the cloud copy of state.
      saveToStorage({ ...s, ...next });
      return next;
    } catch (e) {
      console.warn('Undo failed:', e);
      return {};
    }
  }),

  // ---- Patch AH: full reset to engine defaults ----
  resetToDefaults: () => set(() => {
    // Wipes both in-memory state AND the localStorage entry. Used by
    // AuthGate around sign-in/sign-out to keep the previous user's data
    // from leaking into the next session. The two leak vectors this
    // closes: render-gap UI flash, and the cloud-empty branch upload of
    // snapshotForCloud() into a new account's row.
    //
    // Does NOT push to cloud. AuthGate handles cloud as a separate step
    // after this reset (load existing or upload defaults for brand-new
    // accounts).
    //
    // Patch AI: defaultEtfHoldings/defaultEtfTransactions now return
    // empty arrays, so this reset finally produces a truly clean slate.
    //
    // Patch AJ: also resets welcomed to false. A different account
    // signing in is a different user; they should see the welcome toast
    // on first sign-in just like Paul did. (If the cloud row for that
    // account already has welcomed=true, applyPersistedState will
    // overlay it after this reset.)
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.warn('Failed to clear localStorage on reset', e);
    }
    lastSnapshotKey = '';
    lastSnapshotTime = 0;
    return {
      purchases: [],
      goals: isFIRETenant() ? [
        { id: 'default-fire-1', label: 'Whole coiner', targetBtc: new Decimal('1'), enabled: true },
        { id: 'default-fire-2', label: '2.1 BTC', targetBtc: new Decimal('2.1'), enabled: true },
        { id: 'default-fire-3', label: '10 BTC', targetBtc: new Decimal('10'), enabled: true },
      ] : [],
      liveSnapshot: null,
      lastSeedLoadedAt: null,
      scenarios: [],
      selectedScenarioId: null,
      paceMethod: 'allTimePace',
      futurePlanWindowYears: 5,
      loopLoanInputs: defaultLoopLoanInputs(),
      aaveSnapshot: null,
      defillamaQuery: DEFAULT_DEFILLAMA_QUERY,
      carryInputs: isFIRETenant()
        ? { ...defaultCarryInputs(), strategy: 'carry' as const }
        : defaultCarryInputs(),
      etfHoldings: defaultEtfHoldings(),
      etfTransactions: defaultEtfTransactions(),
      priceModel: WORKBOOK_PRICE_MODEL,
      loanEvents: [],
      salaryPlan: defaultSalaryPlan(),
      // Patch BB: empty by default; users opt in by enabling a schedule
      // for an asset on the Purchases tab. Walker is a no-op when this
      // array is empty.
      autoBuySchedules: [],
      // Patch BJ: empty securities + transactions on reset. Users
      // re-add through the Stocks & ETFs / Metals sub-cards.
      securityAssets: [],
      securityTransactions: [],
      // Patch BL: reset real estate / cash / debt to empty.
      realEstateProperties: [],
      cashEntries: [],
      debtItems: [],
      // Patch BE: reset provider/override to engine defaults. Aave is
      // the default provider (matches pre-BE behavior). Ledn snapshot
      // refills shortly after when App.tsx's boot effect fires.
      selectedLoanProvider: 'aave',
      lednSnapshot: null,
      lednStatus: 'idle',
      lednError: null,
      manualBorrowAprOverride: null,
      welcomed: false,
      // Patch BZ: clear checklist progress on user switch. Cloud row's
      // securityChecklist will be re-applied via applyPersistedState if it
      // exists, just like welcomed.
      securityChecklist: {},
      view: 'dashboard',
      liveStatus: 'idle',
      liveError: null,
      aaveStatus: 'idle',
      aaveError: null,
      etfPriceStatus: 'idle',
      etfPriceError: null,
      undoStack: [],
    };
  }),

  // ---- cloud sync hooks ----
  applyPersistedState: (encoded: unknown) => set(s => {
    const decoded = decodePersistedState(encoded);
    const next: Partial<AppState> = { ...decoded };
    try {
      const merged = { ...s, ...next };
      const payload = encodeStateForPersist(merged);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {
      console.warn('Failed to mirror cloud state to localStorage', e);
    }
    return next;
  }),

  snapshotForCloud: () => {
    const s = get();
    return encodeStateForPersist(s);
  },

  // ---- Patch AS: view-as-client ----
  // The sidecar snapshot key. Distinct from STORAGE_KEY so the admin's
  // own data and the view-as session data don't collide. Cruft cleanup
  // is best-effort: a stray snapshot after a crash gets overwritten by
  // the next enterViewAs, and the cloud-load on next sign-in restores
  // the admin's row regardless of whatever's in localStorage.
  enterViewAs: ({ userId, email, clientState }) => {
    try {
      const currentEncoded = encodeStateForPersist(get());
      localStorage.setItem(STORAGE_KEY + ':admin-snapshot', JSON.stringify(currentEncoded));
    } catch (e) {
      // If we can't snapshot the admin, abort: refusing-to-enter is far
      // better than entering and being unable to exit cleanly.
      console.error('enterViewAs: failed to snapshot admin state', e);
      throw new Error('Could not enter view-as: failed to snapshot your own state.');
    }
    // Apply the client's encoded state through the same decode path that
    // cloud loads use. Then flip the sync gate so any setLiveSnapshot or
    // other passive state change can't propagate to the admin's row.
    // Order matters: gate FIRST, then apply. Otherwise applyPersistedState's
    // internal localStorage write could race with a scheduleCloudSave
    // captured from a setLiveSnapshot mid-transition.
    void import('../io/supabase/sync.js').then(({ setSyncViewingAs }) => {
      setSyncViewingAs(true);
    });
    get().applyPersistedState(clientState);
    set({ viewingAs: { userId, email } });
  },

  exitViewAs: () => {
    // Read the snapshot back and apply. If it's gone (cross-tab edge
    // case, or a crash mid-session that cleared local storage), fall
    // back to resetToDefaults; the caller is expected to trigger a
    // cloud-load (typically by setting view to 'admin' which doesn't
    // require state, then the next AuthGate cycle handles it). We
    // don't auto-reload the page because that's a worse UX than a
    // momentarily-blank view.
    let restored = false;
    try {
      const raw = localStorage.getItem(STORAGE_KEY + ':admin-snapshot');
      if (raw) {
        const parsed = JSON.parse(raw) as unknown;
        get().applyPersistedState(parsed);
        localStorage.removeItem(STORAGE_KEY + ':admin-snapshot');
        restored = true;
      }
    } catch (e) {
      console.warn('exitViewAs: failed to restore admin snapshot', e);
    }
    if (!restored) {
      console.warn('exitViewAs: no snapshot found; resetting to defaults');
      get().resetToDefaults();
    }
    // Clear flag LAST so any debounced save that arrives during the
    // applyPersistedState above stays gated; once viewingAs is null,
    // the gate opens again and normal saves resume.
    set({ viewingAs: null });
    void import('../io/supabase/sync.js').then(({ setSyncViewingAs }) => {
      setSyncViewingAs(false);
    });
  },
}));
